import React from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Tag, Spin } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import {
    TagOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    AppstoreOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/ui";
import dayjs from "dayjs";

export const PriceListShow = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "price",
        id,
        meta: {
            fields: [
                "id",
                "itemCode",
                "inventoryItemId",
                "basePrice",
                "discountType",
                "discountValue",
                "minimumQuantity",
                "maximumQuantity",
                "effectivePrice",
                "startDate",
                "endDate",
                "isActive",
                "currencyCode",
                "createdAt",
                "updatedAt",
                {
                    inventoryItem: [
                        "id",
                        "itemName",
                        "itemCode",
                        "itemClassificationCode",
                        "itemTypeCode",
                        "barcode",
                        "isActive",
                        "additionalInformation"
                    ]
                }
            ]
        }
    });

    const { data, isLoading } = queryResult;
    const record = data?.data;

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!record) {
        return (
            <div className="page-container">
                <PageHeader
                    title="Price Not Found"
                    breadcrumbs={[
                        { title: "Prices", href: "/prices" },
                        { title: "Not Found" },
                    ]}
                />
                <div className="info-card">
                    <div className="info-card-body text-center py-8">
                        <p className="text-text-muted">The requested price could not be found.</p>
                    </div>
                </div>
            </div>
        );
    }

    const formatDiscount = () => {
        if (record.discountType === "None" || !record.discountValue) return "None";
        if (record.discountType === "Percentage") return `${record.discountValue}%`;
        return `${record.currencyCode} ${parseFloat(record.discountValue || 0).toFixed(2)}`;
    };

    return (
        <div className="page-container">
            <PageHeader
                title={`Price: ${record.inventoryItem?.itemName || record.itemCode || 'Details'}`}
                titleIcon={<TagOutlined />}
                breadcrumbs={[
                    { title: "Prices", href: "/prices" },
                    { title: record.inventoryItem?.itemName || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/prices/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Effective Price</div>
                        <div className="mini-stat-value text-primary">
                            {record.currencyCode} {parseFloat(record.effectivePrice || 0).toFixed(2)}
                        </div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Base Price</div>
                        <div className="mini-stat-value">
                            {record.currencyCode} {parseFloat(record.basePrice || 0).toFixed(2)}
                        </div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Currency</div>
                        <div className="mini-stat-value">{record.currencyCode}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record.isActive ? 'active' : 'inactive'}`}>
                            {record.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            {record.isActive ? "Active" : "Inactive"}
                        </div>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Price Details Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Price Details</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Base Price:</span>
                                <span className="info-value">{record.currencyCode} {parseFloat(record.basePrice || 0).toFixed(2)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Effective Price:</span>
                                <span className="info-value font-semibold text-primary">{record.currencyCode} {parseFloat(record.effectivePrice || 0).toFixed(2)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Discount:</span>
                                <span className="info-value">{formatDiscount()}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Currency:</span>
                                <span className="info-value">{record.currencyCode}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Quantity Range:</span>
                                <span className="info-value">{record.minimumQuantity || 1} - {record.maximumQuantity || "∞"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <Tag color={record.isActive ? "success" : "default"}>
                                    {record.isActive ? "Active" : "Inactive"}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Start Date:</span>
                                <span className="info-value">{record.startDate ? dayjs(record.startDate).format("YYYY-MM-DD") : "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">End Date:</span>
                                <span className="info-value">{record.endDate ? dayjs(record.endDate).format("YYYY-MM-DD") : "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created At:</span>
                                <span className="info-value">
                                    <DateField value={record.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Last Updated:</span>
                                <span className="info-value">
                                    <DateField value={record.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Item Details Card */}
                {record.inventoryItem && (
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">
                                <AppstoreOutlined style={{ marginRight: 8 }} />
                                Item Details
                            </h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Item Name:</span>
                                    <span className="info-value font-semibold">{record.inventoryItem.itemName}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Item Code:</span>
                                    <span className="info-value">{record.inventoryItem.itemCode}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Classification:</span>
                                    <span className="info-value">{record.inventoryItem.itemClassificationCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Type:</span>
                                    <span className="info-value">{record.inventoryItem.itemTypeCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Barcode:</span>
                                    <span className="info-value">{record.inventoryItem.barcode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Item Status:</span>
                                    <Tag color={record.inventoryItem.isActive ? "success" : "default"}>
                                        {record.inventoryItem.isActive ? "Active" : "Inactive"}
                                    </Tag>
                                </div>
                                {record.inventoryItem.additionalInformation && (
                                    <div className="info-item full-width">
                                        <span className="info-label">Additional Information:</span>
                                        <span className="info-value">{record.inventoryItem.additionalInformation}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
